import { createFileRoute } from "@tanstack/react-router";
import { Book, HelpCircle, Mail } from "lucide-react";
import { PageLayout } from "../../components/PageLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";

export const Route = createFileRoute("/_layout/help")({
    head: () => ({
        meta: [
            { name: "title", content: "Help - StoryDoodle" },
            {
                name: "description",
                content: "Need help with StoryDoodle? Learn how to play and get answers to your questions!",
            },
            { property: "og:title", content: "Help - StoryDoodle" },
            {
                property: "og:description",
                content: "Need help with StoryDoodle? Learn how to play and get answers to your questions!",
            },
            { property: "og:url", content: "https://storydoodle.app/help" },
        ],
    }),
    component: Help,
});

function Help() {
    const handleEmailHelp = () => {
        window.location.href = "mailto:help@storydoodle.app?subject=I need help with StoryDoodle";
    };

    return (
        <PageLayout title="Help" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Help" }]}>
            <main className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
                <div className="space-y-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-foreground mb-4">Need Help?</h1>
                        <p className="text-muted-foreground text-lg">
                            Don't worry! We're here to help you have fun with StoryDoodle!
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Ask for Help */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Mail className="h-5 w-5" />
                                    Ask for Help
                                </CardTitle>
                                <CardDescription>Send us a message and we'll help you!</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                    Having trouble? Something not working? Send us an email and we'll help you out!
                                </p>
                                <Button onClick={handleEmailHelp} className="w-full">
                                    <Mail className="h-4 w-4 mr-2" />
                                    Send Help Message
                                </Button>
                                <p className="text-xs text-muted-foreground text-center">help@storydoodle.app</p>
                            </CardContent>
                        </Card>

                        {/* How to Play */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Book className="h-5 w-5" />
                                    How to Play
                                </CardTitle>
                                <CardDescription>It's easy and fun!</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 bg-primary text-primary rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                                            1
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">Pick a Story</p>
                                            <p className="text-xs text-muted-foreground">
                                                Tap "Create" and pick a fun story to start!
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 bg-primary text-primary rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                                            2
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">Draw Something Cool</p>
                                            <p className="text-xs text-muted-foreground">
                                                Use your finger to draw what you think should happen!
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 bg-primary text-primary rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                                            3
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">Watch the Magic</p>
                                            <p className="text-xs text-muted-foreground">
                                                The computer looks at your drawing and continues your story!
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* FAQ Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <HelpCircle className="h-5 w-5" />
                                Questions & Answers
                            </CardTitle>
                            <CardDescription>Here are answers to questions kids often ask!</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-medium text-foreground mb-2">
                                        How does the computer know what I drew?
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        The computer is really smart! It looks at your drawing and tries to figure out
                                        what you made, then uses that to make your story even more exciting!
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-medium text-foreground mb-2">Can I save my stories?</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Yes! All your awesome stories are saved automatically. You can see them anytime
                                        by clicking "My Stories" in the navigation!
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-medium text-foreground mb-2">Is StoryDoodle safe for kids?</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Yes! StoryDoodle is made just for kids and families. All the stories are
                                        kid-friendly and safe. We keep your drawings and stories private and secure.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-medium text-foreground mb-2">
                                        What if I can't make more stories?
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        You can create several stories each day, and your story count resets every day!
                                        Check Settings to see how many stories you have left.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="font-medium text-foreground mb-2">
                                        What if my drawing doesn't look perfect?
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        That's totally okay! Your drawings don't need to be perfect - the computer is
                                        really good at understanding what you're trying to draw. Just have fun and be
                                        creative!
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </PageLayout>
    );
}
